import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClients } from '@/app/ApiProvider';
import type { components } from '@/api/types/api-gateway';

// SOnboarding — wizard backend hooks (PR-9 resource).
export type OnboardingState = components['schemas']['OnboardingState'];
export type OnboardingPatch = components['schemas']['OnboardingPatch'];

export function useOnboardingState() {
  const clients = useApiClients();
  return useQuery({
    queryKey: ['onboarding', 'state'],
    queryFn: async (): Promise<OnboardingState> => {
      const { data, error } = await clients.apiGateway.GET('/v1/tenants/me/onboarding');
      if (error || !data) throw new Error('onboarding state failed');
      return data;
    },
    staleTime: 30_000,
  });
}

export function usePatchOnboarding() {
  const clients = useApiClients();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: OnboardingPatch) => {
      const { data, error } = await clients.apiGateway.PATCH('/v1/tenants/me/onboarding', {
        body: patch,
      });
      if (error) throw new Error('patch onboarding failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding'] }),
  });
}

export function useCreateSite() {
  const clients = useApiClients();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      name: string;
      address?: string;
      timezone?: string;
    }) => {
      const { data, error } = await clients.apiGateway.POST('/v1/sites', {
        body: vars,
        params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
      });
      if (error) throw new Error('create site failed');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

type InvitationRole = 'viewer' | 'maintainer' | 'manager' | 'admin';

export function useCreateInvitation() {
  const clients = useApiClients();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; role: InvitationRole }) => {
      const { data, error } = await clients.apiGateway.POST('/v1/invitations', {
        body: vars,
      });
      if (error) throw new Error('create invitation failed');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] });
      qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}
